<script setup lang="ts">
import ApplicationStepHeader from '@/components/common/application-step-header.vue';
import ProductSearch from '@/components/goods-view/components/product-search.vue';
import ProductResult from '@/components/goods-view/components/product-result.vue';
import {useProductStore} from '@/store/good';
import {storeToRefs} from 'pinia';
import ProductCart from '@/components/goods-view/components/product-cart.vue';
import useCurrentSearch from '@/components/goods-view/searchDataStore';
import {useApplicationStore} from '@/store/application';
import SummaryBanner from '@/components/SummaryBanner.vue';
import { ref, watch, onUnmounted } from 'vue';

const productStore = useProductStore();
const {haveSearched} = useCurrentSearch();
const {totalGoods, isProductStepValid} = storeToRefs(productStore);
const { hasValidation } = storeToRefs(useApplicationStore());
const loading = ref(false);

// iOS-safe body overflow handling
const setBodyOverflow = (hidden: boolean) => {
    const body = document.body;
    if (hidden) {
        // Store current scroll position
        const scrollY = window.scrollY;
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        // Store scroll position for restoration
        body.dataset.scrollY = scrollY.toString();
    } else {
        // Restore scroll position and remove fixed positioning
        const scrollY = parseInt(body.dataset.scrollY || '0');
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        body.style.overflow = '';
        delete body.dataset.scrollY;
        // Restore scroll position
        window.scrollTo(0, scrollY);
    }
};

watch(loading, (newValue) => {
    setBodyOverflow(newValue);
});

// Cleanup on component unmount
onUnmounted(() => {
    if (loading.value) {
        setBodyOverflow(false);
    }
});
</script>

<template>
    <div>
        <StfCard :success="isProductStepValid" :error="hasValidation && !isProductStepValid">
            <template #title>
                <ApplicationStepHeader title="Your Products & Services" :help="true" help-title="Your Products & Services">
                    <template #help>
                        <div class="mb-2">A trademark gives you the exclusive right to use your brand for the products and services covered in the application.</div>
                        <div class="mb-2">Products and services are grouped into different categories/classes. The Trademark Office charges a fee for each extra class you select, but you can cover multiple items within each class at no extra cost.</div>
                        <div class="mb-2">Search for keywords that describe the products and/or services you use your trademark for. When thinking of search terms, ask yourself:</div>
                        <div class="mb-2">
                            <ol class="indented-list">
                                <li>What products do I sell?</li>
                                <li>What services do I provide to people?</li>
                            </ol>
                        </div>
                        <div>The search results will show descriptions that have already been approved by the Trademark Office - sometimes these might not exactly match how you describe your products/services, but you should be able to find something close.</div>
                    </template>
                </ApplicationStepHeader>
            </template>
            <template #content>
                <product-search @loading-change="(newValue) => loading = newValue"/>
                <div v-if="loading" class="py-6">
                    <div class="stf-spinner stf-spinner--large m-auto"></div>
                    <div class="has-text-centered mt-4">Searching for results...</div>
                </div>
                <template v-if="haveSearched && !loading">
                    <StfDivider/>
                    <product-result class="my-2"/>
                </template>
                <template v-if="totalGoods > 0">
                    <product-cart/>
                </template>
            </template>
        </StfCard>
        <SummaryBanner />
    </div>
</template>

<style scoped>
.indented-list {
    margin-left: 30px;
}
</style>
